import { useState, useCallback } from 'react'
import { salesService } from '../../services/api'
import { useAuth } from '../auth/AuthProvider'

interface InvoiceNavigationState {
  currentInvoice: any | null
  isLoading: boolean
  error: string | null
  hasNext: boolean
  hasPrevious: boolean
}

interface UseInvoiceNavigationProps {
  branchId: number
  onInvoiceChange?: (invoice: any) => void
}

export function useInvoiceNavigation({ branchId, onInvoiceChange }: UseInvoiceNavigationProps) {
  const { isAuthenticated } = useAuth()
  
  const [state, setState] = useState<InvoiceNavigationState>({
    currentInvoice: null,
    isLoading: false,
    error: null,
    hasNext: false,
    hasPrevious: false
  })

  // Load invoice by bond_num
  const loadInvoice = useCallback(async (bondNum: string) => {
    if (!isAuthenticated || !bondNum || !branchId) {
      return null
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      // Use list API with bond_num search instead of non-existent show API
      const response = await salesService.list({
        def_branch: branchId,
        bond_num: bondNum,  // Search by specific bond number
        ordering: 'asc'
      })
      
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const invoiceData = response.data[0]  // Get first (and should be only) result
        console.log('📥 Loaded invoice by bond_num:', bondNum, '→', invoiceData)
        
        setState(prev => ({ 
          ...prev, 
          currentInvoice: invoiceData,
          isLoading: false 
        }))
        
        if (onInvoiceChange) {
          onInvoiceChange(invoiceData)
        }
        
        return invoiceData
      } else {
        throw new Error(`No invoice found with bond_num: ${bondNum}`)
      }
    } catch (error) {
      console.error('Failed to load invoice:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `فشل في تحميل فاتورة رقم ${bondNum}`
      }))
    }
    
    return null
  }, [isAuthenticated, branchId, onInvoiceChange])

  // Navigate to specific invoice by bond_num
  const navigateToInvoice = useCallback(async (bondNum: string) => {
    return await loadInvoice(bondNum)
  }, [loadInvoice])

  // Load the first available invoice for the branch
  const loadFirstInvoice = useCallback(async () => {
    console.log('=== LOAD FIRST INVOICE CALLED ===')
    console.log('isAuthenticated:', isAuthenticated)
    console.log('branchId:', branchId)
    
    if (!isAuthenticated || !branchId) {
      console.log('❌ Authentication or branchId missing')
      return null
    }

    try {
      console.log('📞 Making API call to salesService.list...')
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const response = await salesService.list({
        def_branch: branchId,
        ordering: 'asc',
        page: 1
      })
      
      console.log('📥 API Response received:', response)
      console.log('🔍 Checking response.data:', response?.data)
      console.log('🔍 Checking response.data.data:', response?.data?.data)
      console.log('🔍 response.data.data length:', response?.data?.data?.length)
      
      // Check if the data is directly in response.data (Laravel pagination)
      console.log('🔍 Checking direct response.data as array:', Array.isArray(response?.data))
      console.log('🔍 Direct response.data length:', response?.data?.length)
      
      // Laravel pagination response structure: response.data is the invoice array directly
      // Based on API client implementation, response.data contains the invoice array
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const firstInvoice = response.data[0]
        console.log('✅ First invoice from API:', firstInvoice)
        
        // Update state and call onInvoiceChange directly with the invoice data
        setState(prev => ({ 
          ...prev, 
          currentInvoice: firstInvoice,
          isLoading: false 
        }))
        
        console.log('🔄 Calling onInvoiceChange with first invoice')
        if (onInvoiceChange) {
          onInvoiceChange(firstInvoice)
        }
        
        return firstInvoice
      } else {
        console.log('❌ No invoices found in response')
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'لا توجد فواتير متاحة'
        }))
      }
    } catch (error) {
      console.error('❌ Failed to load first invoice:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'فشل في تحميل الفاتورة الأولى'
      }))
    }
    
    return null
  }, [isAuthenticated, branchId, onInvoiceChange])

  // Navigate to next invoice (simplified version - in a real implementation, 
  // you might want to maintain a local cache of invoice order)
  const navigateNext = useCallback(async () => {
    if (!state.currentInvoice) return
    
    // This is a simplified implementation
    // In practice, you'd want to get the next invoice in the sequence
    try {
      const response = await salesService.list({
        def_branch: branchId,
        ordering: 'asc',
        page: 1 // This should be calculated based on current position
      })
      
      if (response?.data?.data && response.data.data.length > 0) {
        // Find current invoice index and get next
        const currentIndex = response.data.data.findIndex(
          (inv: any) => inv.bond_num === state.currentInvoice?.bond_num
        )
        
        if (currentIndex >= 0 && currentIndex < response.data.data.length - 1) {
          const nextInvoice = response.data.data[currentIndex + 1]
          await loadInvoice(nextInvoice.bond_num)
        }
      }
    } catch (error) {
      console.error('Failed to navigate to next invoice:', error)
      setState(prev => ({
        ...prev,
        error: 'فشل في التنقل إلى الفاتورة التالية'
      }))
    }
  }, [state.currentInvoice, branchId, loadInvoice])

  // Navigate to previous invoice
  const navigatePrevious = useCallback(async () => {
    if (!state.currentInvoice) return
    
    try {
      const response = await salesService.list({
        def_branch: branchId,
        ordering: 'asc',
        page: 1
      })
      
      if (response?.data?.data && response.data.data.length > 0) {
        const currentIndex = response.data.data.findIndex(
          (inv: any) => inv.bond_num === state.currentInvoice?.bond_num
        )
        
        if (currentIndex > 0) {
          const previousInvoice = response.data.data[currentIndex - 1]
          await loadInvoice(previousInvoice.bond_num)
        }
      }
    } catch (error) {
      console.error('Failed to navigate to previous invoice:', error)
      setState(prev => ({
        ...prev,
        error: 'فشل في التنقل إلى الفاتورة السابقة'
      }))
    }
  }, [state.currentInvoice, branchId, loadInvoice])

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return {
    currentInvoice: state.currentInvoice,
    isLoading: state.isLoading,
    error: state.error,
    hasNext: state.hasNext,
    hasPrevious: state.hasPrevious,
    navigateToInvoice,
    loadFirstInvoice,
    navigateNext,
    navigatePrevious,
    clearError
  }
}
